import { useReducer, useEffect, useCallback } from 'react';
import type { PlayerAssignment, ValidationError, HypothesisSnapshot, GameLevel, PuzzleRole } from '../types/game';
import {
  initAssignments,
  nextRole,
  validateAll,
  compareSolution,
} from '../lib/gameEngine';

interface GameEngineState {
  levelId: string | null;
  level: GameLevel | null;
  assignments: PlayerAssignment[];
  checkedHints: string[];
  validationErrors: ValidationError[];
  isSolved: boolean;
  visibleDay: number;
  hypothesisStack: HypothesisSnapshot[];
  focusedSeat: number | null;
}

type GameAction =
  | { type: 'LOAD_LEVEL'; level: GameLevel }
  | { type: 'SET_ROLE'; seat: number; role: PuzzleRole }
  | { type: 'CYCLE_ROLE'; seat: number }
  | { type: 'TOGGLE_HINT'; hintId: string }
  | { type: 'CHECK_SOLUTION' }
  | { type: 'RESET_LEVEL' }
  | { type: 'SET_TIMELINE_DAY'; day: number }
  | { type: 'PUSH_HYPOTHESIS'; label: string }
  | { type: 'POP_HYPOTHESIS' }
  | { type: 'SET_FOCUSED_SEAT'; seat: number | null };

const initialState: GameEngineState = {
  levelId: null,
  level: null,
  assignments: [],
  checkedHints: [],
  validationErrors: [],
  isSolved: false,
  visibleDay: 0,
  hypothesisStack: [],
  focusedSeat: null,
};

function storageKey(levelId: string) {
  return `game:assignments:${levelId}`;
}

function loadFromStorage(levelId: string, level: GameLevel): PlayerAssignment[] {
  try {
    const saved = localStorage.getItem(storageKey(levelId));
    if (saved) {
      const parsed = JSON.parse(saved) as PlayerAssignment[];
      if (Array.isArray(parsed) && parsed.length === 10) {
        const fresh = initAssignments(level);
        return fresh.map((f) => {
          const s = parsed.find((p) => p.seat === f.seat);
          return s ? { ...f, role: s.role } : f;
        });
      }
    }
  } catch { /* ignore */ }
  return initAssignments(level);
}

function saveToStorage(levelId: string, assignments: PlayerAssignment[]) {
  try { localStorage.setItem(storageKey(levelId), JSON.stringify(assignments)); } catch { /* ignore */ }
}

function saveCompletion(levelId: string) {
  try {
    const raw = localStorage.getItem('game:completed');
    const map = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    map[levelId] = true;
    localStorage.setItem('game:completed', JSON.stringify(map));
  } catch { /* ignore */ }
}

function reducer(state: GameEngineState, action: GameAction): GameEngineState {
  switch (action.type) {
    case 'LOAD_LEVEL': {
      const level = action.level;
      const assignments = loadFromStorage(level.id, level);
      const errors = validateAll(assignments, level);
      return {
        ...initialState,
        levelId: level.id,
        level,
        assignments,
        validationErrors: errors,
        isSolved: compareSolution(assignments, level.solution),
      };
    }

    case 'SET_ROLE': {
      if (!state.level) return state;
      const assignments = state.assignments.map((a) =>
        a.seat === action.seat ? { ...a, role: action.role } : a,
      );
      const errors = validateAll(assignments, state.level);
      saveToStorage(state.levelId!, assignments);
      return { ...state, assignments, validationErrors: errors, isSolved: false };
    }

    case 'CYCLE_ROLE': {
      if (!state.level) return state;
      const assignments = state.assignments.map((a) =>
        a.seat === action.seat ? { ...a, role: nextRole(a.role) } : a,
      );
      const errors = validateAll(assignments, state.level);
      saveToStorage(state.levelId!, assignments);
      return { ...state, assignments, validationErrors: errors, isSolved: false };
    }

    case 'TOGGLE_HINT': {
      const isChecked = state.checkedHints.includes(action.hintId);
      return {
        ...state,
        checkedHints: isChecked
          ? state.checkedHints.filter((id) => id !== action.hintId)
          : [...state.checkedHints, action.hintId],
      };
    }

    case 'CHECK_SOLUTION': {
      if (!state.level) return state;
      const isSolved = compareSolution(state.assignments, state.level.solution);
      if (isSolved) saveCompletion(state.levelId!);
      return { ...state, isSolved };
    }

    case 'RESET_LEVEL': {
      if (!state.level) return state;
      try { localStorage.removeItem(storageKey(state.levelId!)); } catch { /* ignore */ }
      return {
        ...state,
        assignments: initAssignments(state.level),
        checkedHints: [],
        validationErrors: [],
        isSolved: false,
        hypothesisStack: [],
        focusedSeat: null,
      };
    }

    case 'SET_TIMELINE_DAY':
      return { ...state, visibleDay: action.day };

    case 'PUSH_HYPOTHESIS': {
      const snapshot: HypothesisSnapshot = {
        id: `${Date.now()}`,
        label: action.label,
        assignments: JSON.parse(JSON.stringify(state.assignments)),
        checkedHints: [...state.checkedHints],
      };
      return { ...state, hypothesisStack: [...state.hypothesisStack, snapshot] };
    }

    case 'POP_HYPOTHESIS': {
      if (state.hypothesisStack.length === 0) return state;
      const stack = [...state.hypothesisStack];
      const snapshot = stack.pop()!;
      const errors = state.level ? validateAll(snapshot.assignments, state.level) : [];
      return {
        ...state,
        assignments: snapshot.assignments,
        checkedHints: snapshot.checkedHints,
        validationErrors: errors,
        isSolved: false,
        hypothesisStack: stack,
      };
    }

    case 'SET_FOCUSED_SEAT':
      return { ...state, focusedSeat: action.seat };

    default:
      return state;
  }
}

export function useGameEngine(level: GameLevel) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    dispatch({ type: 'LOAD_LEVEL', level });
  // Re-load only when the level ID changes, not on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level.id]);

  const setRole = useCallback((seat: number, role: PuzzleRole) => dispatch({ type: 'SET_ROLE', seat, role }), []);
  const cycleRole = useCallback((seat: number) => dispatch({ type: 'CYCLE_ROLE', seat }), []);
  const toggleHint = useCallback((hintId: string) => dispatch({ type: 'TOGGLE_HINT', hintId }), []);
  const checkSolution = useCallback(() => dispatch({ type: 'CHECK_SOLUTION' }), []);
  const resetLevel = useCallback(() => dispatch({ type: 'RESET_LEVEL' }), []);
  const setTimelineDay = useCallback((day: number) => dispatch({ type: 'SET_TIMELINE_DAY', day }), []);
  const pushHypothesis = useCallback((label: string) => dispatch({ type: 'PUSH_HYPOTHESIS', label }), []);
  const popHypothesis = useCallback(() => dispatch({ type: 'POP_HYPOTHESIS' }), []);
  const setFocusedSeat = useCallback((seat: number | null) => dispatch({ type: 'SET_FOCUSED_SEAT', seat }), []);

  return {
    state,
    setRole,
    cycleRole,
    toggleHint,
    checkSolution,
    resetLevel,
    setTimelineDay,
    pushHypothesis,
    popHypothesis,
    setFocusedSeat,
  };
}

export function getCompletedLevels(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem('game:completed');
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch { return {}; }
}
